'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useId, useMemo, useRef } from 'react';
import { useStableCallback } from '../../lib/hooks/useStableCallback';
const LmfAnalyticsContext = createContext(undefined);
export const LmfAnalyticsProvider = ({ children, title, track, id, category, }) => {
    const uuid = useId();
    const parentContext = useLmfAnalyticsContext();
    const _descendantIdsRef = useRef([]);
    useEffect(() => {
        if (!parentContext._descendantIds.includes(uuid)) {
            parentContext._descendantIds.push(uuid);
        }
        return () => {
            const index = parentContext._descendantIds.indexOf(uuid);
            if (index > -1) {
                parentContext._descendantIds.splice(index, 1);
            }
        };
    }, [uuid, parentContext._descendantIds]);
    const getContextChain = useStableCallback(() => {
        const index = parentContext._descendantIds.indexOf(uuid);
        const total = parentContext._descendantIds.length;
        return [
            ...parentContext.getContextChain(),
            {
                title,
                id,
                category,
                siblingPosition: index + 1,
                siblingTotal: total,
            },
        ];
    });
    const value = useMemo(() => {
        return {
            getContextChain,
            track: track ?? parentContext.track,
            _descendantIds: _descendantIdsRef.current,
        };
    }, [getContextChain, parentContext.track, track]);
    return _jsx(LmfAnalyticsContext.Provider, { value: value, children: children });
};
export const useLmfAnalyticsContext = () => {
    const context = useContext(LmfAnalyticsContext);
    return useMemo(() => {
        return (context ?? {
            id: undefined,
            getContextChain: () => [],
            track: () => { },
            _descendantIds: [],
        });
    }, [context]);
};
export const useLmfAnalyticsOnSwipe = () => {
    const lmfAnalyticsContext = useLmfAnalyticsContext();
    return useStableCallback(({ action, element, variant, label, }) => {
        lmfAnalyticsContext.track({
            action,
            element,
            variant,
            label,
            context: lmfAnalyticsContext.getContextChain(),
            href: undefined,
        });
    });
};
export const useLmfAnalyticsOnClick = ({ onClick, label, variations, ...trackingProps }) => {
    const lmfAnalyticsContext = useLmfAnalyticsContext();
    const variant = variations.length > 0 ? variations.join(':') : 'default';
    return useStableCallback((...args) => {
        const computedLabel = typeof label === 'string' ? label : (args[0]?.currentTarget ?? args[0]?.target)?.textContent;
        lmfAnalyticsContext.track({
            ...trackingProps,
            action: 'click',
            label: computedLabel ?? 'NULL',
            variant,
            context: lmfAnalyticsContext.getContextChain(),
        });
        const response = onClick?.(...args);
        return response;
    });
};
export const useLmfAnalyticsOnOpen = ({ isOpen, element, variant, label, }) => {
    const lmfAnalyticsContext = useLmfAnalyticsContext();
    const previousOpenState = useRef(isOpen);
    if (previousOpenState.current !== isOpen) {
        lmfAnalyticsContext.track({
            action: isOpen ? 'open' : 'close',
            element,
            variant,
            label,
            context: lmfAnalyticsContext.getContextChain(),
            href: undefined,
        });
    }
    previousOpenState.current = isOpen;
};
//# sourceMappingURL=LmfAnalyticsProvider.js.map